<template>
    <v-dialog :model-value="show"
              :persistent="true"
    >
        <v-card :rounded="true"
                :border="true"
                :flat="true"
        >
            <v-card-title class="d-flex align-center">
                <v-icon icon="mdi-gift" color="primary" class="mr-2" />
                {{ title }}
            </v-card-title>
            <v-divider />
            <v-card-text style="max-height: 60vh; overflow-y: auto;">
                <v-alert v-if="isTutorialComplete"
                         density="compact"
                         color="success"
                         variant="tonal"
                         class="mb-3"
                >
                    <v-list-item class="px-0" :lines="false">
                        <template #prepend>
                            <v-icon icon="mdi-trophy" color="warning" class="mr-3" />
                        </template>
                        <v-list-item-title class="text-caption text-medium-emphasis">Congratulations!</v-list-item-title>
                        <v-list-item-subtitle class="font-weight-medium text-body-2">You've completed the tutorial! Here's a bonus to get you started on your journey. Come back every day to earn login rewards and grow stronger.</v-list-item-subtitle>
                    </v-list-item>
                </v-alert>
                <v-alert v-else-if="isFirstLogin"
                         density="compact"
                         color="success"
                         variant="tonal"
                         class="mb-3"
                >
                    <v-list-item class="px-0" :lines="false">
                        <template #prepend>
                            <v-icon icon="mdi-hand-wave" color="warning" class="mr-3" />
                        </template>
                        <v-list-item-title class="text-caption text-medium-emphasis">Welcome!</v-list-item-title>
                        <v-list-item-subtitle class="font-weight-medium text-body-2">Here are some rewards to get you started. Log in daily to receive bonus rewards!</v-list-item-subtitle>
                    </v-list-item>
                </v-alert>
                <v-alert v-else-if="isSubscriptionPurchase"
                         density="compact"
                         color="success"
                         variant="tonal"
                         class="mb-3"
                >
                    <v-list-item class="px-0" :lines="false">
                        <template #prepend>
                            <v-icon icon="mdi-crown" color="warning" class="mr-3" />
                        </template>
                        <v-list-item-title class="text-caption text-medium-emphasis">Subscription Active!</v-list-item-title>
                        <v-list-item-subtitle class="font-weight-medium text-body-2">Thanks for subscribing! Here are today's subscription rewards. Log in daily to keep earning them.</v-list-item-subtitle>
                    </v-list-item>
                </v-alert>
                <div v-if="streak !== null && !isFirstLogin && !isSubscriptionPurchase">
                    <!-- Streak Counter -->
                    <div class="d-flex align-center mb-3">
                        <v-icon icon="mdi-fire" color="warning" size="large" class="mr-2" />
                        <span class="text-h4 font-weight-bold mr-2">{{ streak }}</span>
                        <span class="text-medium-emphasis">day{{ streak !== 1 ? 's' : '' }} in a row</span>
                    </div>

                    <!-- 7-Day Progress Indicator -->
                    <div class="d-flex justify-space-between mb-3">
                        <div v-for="day in 7"
                             :key="day"
                             class="text-center"
                             :style="{opacity: day <= streak ? 1 : 0.4}"
                        >
                            <v-icon :icon="day <= streak ? 'mdi-fire' : 'mdi-circle-outline'"
                                    :color="day <= streak ? 'warning' : 'grey'"
                                    size="large"
                            />
                            <div class="text-caption" :class="day === streak ? 'font-weight-bold text-success' : 'text-medium-emphasis'">
                                {{ day }}
                            </div>
                        </div>
                    </div>

                    <v-alert density="compact"
                             variant="tonal"
                             color="warning"
                             class="my-3"
                    >
                        Log in every day to keep your streak going and earn better and better rewards!
                    </v-alert>
                </div>
                <div v-for="(group, index) in login_rewards"
                     :key="index"
                     :class="{'mt-4': index > 0}"
                >
                    <v-chip size="small"
                            color="secondary"
                            variant="tonal"
                    >
                        {{ sourceLabel(group.source) }}
                    </v-chip>
                    <div class="d-flex justify-center">
                        <rewards :rewards="group.rewards" />
                    </div>
                </div>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-spacer />
                <v-btn class="elevation-3" color="success"
                       variant="tonal"
                       @click="onContinue"
                       :prepend-icon="isFirstLogin || isTutorialComplete ? 'mdi-arrow-right' : 'mdi-check'"
                >
                    {{ isFirstLogin || isTutorialComplete ? 'Let\'s Go' : 'OK' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
    import {computed} from "vue";
    import Rewards from "@/components/Rewards";
    import {storeToRefs} from "pinia";
    import {useMainStore} from "@/store";

    defineProps({
        show: {
            type: Boolean,
        },
    });

    const store = useMainStore();
    const {login_rewards, streak} = storeToRefs(store);

    const isFirstLogin = computed(() => {
        return login_rewards.value.some(g => g.source === 'first_login');
    });

    const isTutorialComplete = computed(() => {
        return login_rewards.value.some(g => g.source === 'tutorial_complete');
    });

    const isSubscriptionPurchase = computed(() => {
        return login_rewards.value.some(g => g.source === 'subscription_purchase');
    });

    const title = computed(() => 'Login Rewards');

    const sourceLabels = {
        daily_login: 'Daily Login',
        guild_daily_login: 'Guild Daily Login',
        subscription_daily_login: 'Subscription Daily Login',
        subscription_purchase: 'Subscription Daily Login',
        first_login: 'Welcome Bonus',
        tutorial_complete: 'Tutorial Bonus',
    };

    function sourceLabel(source) {
        return sourceLabels[source] || source;
    }

    function onContinue() {
        store.RESET_PROPERTY('login_rewards');
    }
</script>
